import React, { useState, useRef, useEffect, useCallback } from 'react';
import { GripVertical } from 'lucide-react';

const ResizablePanel = ({ 
  children, 
  defaultWidth = 288, 
  minWidth = 200, 
  maxWidth = 600,
  side = 'left', // 'left' or 'right'
  isVisible = true,
  onResize,
  storageKey,
  isDarkMode,
  usePercentageConstraints = false // New prop to enable percentage-based constraints
}) => {
  const [width, setWidth] = useState(() => {
    if (storageKey) {
      const saved = localStorage.getItem(storageKey);
      return saved ? parseInt(saved, 10) : defaultWidth;
    }
    return defaultWidth;
  });
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate min/max widths based on percentage if enabled
  const getMinMaxWidths = useCallback(() => {
    if (usePercentageConstraints) {
      const minPercent = 1/6; // 1/6th of screen
      const maxPercent = 1/3; // 1/3rd of screen
      return {
        min: windowWidth * minPercent,
        max: windowWidth * maxPercent
      };
    }
    return { min: minWidth, max: maxWidth };
  }, [usePercentageConstraints, windowWidth, minWidth, maxWidth]);

  // Enforce constraints when window resizes (only when windowWidth changes)
  const prevWindowWidthRef = useRef(windowWidth);
  useEffect(() => {
    if (usePercentageConstraints && windowWidth > 0 && prevWindowWidthRef.current !== windowWidth) {
      const minPercent = 1/6; // 1/6th of screen
      const maxPercent = 1/3; // 1/3rd of screen
      const min = windowWidth * minPercent;
      const max = windowWidth * maxPercent;
      setWidth(prevWidth => {
        const constrainedWidth = Math.max(min, Math.min(max, prevWidth));
        return constrainedWidth;
      });
      prevWindowWidthRef.current = windowWidth;
    }
  }, [windowWidth, usePercentageConstraints]);

  // Save width to localStorage
  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(storageKey, width.toString());
    }
    if (onResize) {
      onResize(width);
    }
  }, [width, storageKey, onResize]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [width]);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return;

    const deltaX = side === 'left' 
      ? e.clientX - startXRef.current 
      : startXRef.current - e.clientX;
    
    const { min, max } = getMinMaxWidths();
    const newWidth = Math.max(min, Math.min(max, startWidthRef.current + deltaX));
    setWidth(newWidth);
  }, [isResizing, side, getMinMaxWidths]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      ref={panelRef}
      className="relative flex-shrink-0 h-full"
      style={{ width: `${width}px` }}
    >
      {children}
      {/* Resize Handle */}
      <div
        onMouseDown={handleMouseDown}
        className={`absolute top-0 bottom-0 w-1 cursor-col-resize z-20 ${
          side === 'left' ? 'right-0' : 'left-0'
        } ${
          isDarkMode ? 'hover:bg-blue-600/50' : 'hover:bg-blue-500/50'
        } transition-colors group`}
        style={{
          cursor: isResizing ? 'col-resize' : 'col-resize'
        }}
      >
        <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity ${
          isDarkMode ? 'text-blue-400' : 'text-blue-600'
        }`}>
          <GripVertical size={16} />
        </div>
      </div>
    </div>
  );
};

export default ResizablePanel;
